<template>
  <table class="do-table do-table__user-employments"
         :class="{'do-table__user-employments--import': isSalaryIntegrationActive}">
    <thead>
    <th class="th-check" @click="$emit('toggleAllEmployments')" :title="selectedEmploymentIds.length === 0 ? $t('vue2.tagging.select_all') : $t('vue2.tagging.unselect_all')">
      <div class="check-wrapper">
        <input
          type="checkbox"
          :checked="isAllVisibleSelected"
          :indeterminate.prop="isIndeterminateVisible"
          @click.stop
          readonly
        >
      </div>
    </th>
    <th>Navn</th>
    <th>CPR</th>
    <th>Ansættelse</th>
    <th>Periode</th>
    <th class="td-percent">Beskæftigelse</th>
    <th>Organisation</th>
    <th>Stillingskategori</th>
    <th class="td-subsalary">Vikarløn</th>
    <th>Std. Lønkode</th>
    <th>Std. Omkostningsted</th>
    <th v-if="rowActionLabel" class="th-row-actions"></th>
    </thead>

    <tbody ref="d-b">
    <!-- Integration-visning: grupperet pr. bruger -->
    <template v-if="isSalaryIntegrationActive" v-for="user in filteredUsers">
      <tbody :key="'user-' + user.id" class="user-group">
      <tr
        v-for="employment in user.v_employments"
        :key="'employment-' + employment.id"
      >
        <!-- Checkbox pr. ANSÆTTELSE -->
        <td class="td-check" @click.stop="$emit('toggleEmployment', employment)" title="Vælg/fravælg ansættelse">
          <div class="check-wrapper">
            <input
              type="checkbox"
              :checked="isEmploymentSelected(employment.id)"
              @click.stop
              readonly
            >
          </div>
        </td>

        <!-- Navn vises på alle rækker for overskuelighed -->
        <td :class="{'td-organisation': !hasStillingskategori(employment)}">
          <div class="name-cell">
            <strong>{{ user.name }}</strong>
          </div>
        </td>
        <td class="td-ssn" :class="{'td-organisation': !!user.ssn}">
          <span v-if="user.ssn">{{ formatSsn(user.ssn) }}</span>
          <span v-else class="text-link" @click.stop="$emit('editSsn', user)">Indtast CPR</span>
        </td>
        <td class="td-organisation">
          <template v-if="isShowEmploymentNumber">{{ employment.employment_number }} -</template>
          {{ employment.source_title ? employment.source_title : employment.title }}
        </td>
        <td class="td-organisation td-period">{{ formatEmploymentPeriod(employment) }}</td>
        <td class="td-organisation td-percent">{{ formatEmploymentPercent(employment.employment_percent) }}</td>
        <td class="td-organisation">{{ employment.organisation }}</td>

        <td>
              <span class="d-tag d-tag--inline"
                    :class="{
                      'd-tag--from-other-school': isStillingskategoriFromOtherSchool(employment),
                      'd-tag--inline--clickable': !isStillingskategoriFromOtherSchool(employment)
                    }"
                    v-if="hasStillingskategori(employment)"
                    :content="isStillingskategoriFromOtherSchool(employment) ? 'Kommer fra anden skole' : null"
                    v-tippy="isStillingskategoriFromOtherSchool(employment) ? {
                      arrow: true,
                      animateFill: false,
                      animation: 'shift-away',
                      zIndex: '999999992',
                      boundary: 'window'
                    } : { trigger: 'manual' }"
                    @click.stop="onStillingskategoriClick(user, employment)">
                {{ employment.title }}
              </span>
        </td>
        <td class="td-subsalary">
            <span class="do-pill do-pill--small do-custom-pill do-pill--yellow "
                  v-if="showEmploymentSalaryFields(employment)">Vikarløn
            </span>
        </td>
        <td>
          <span class="do-pill do-pill--small do-custom-pill do-pill--multiline"
                v-if="showEmploymentSalaryFields(employment)">
            {{ getDefaultSalaryCodeName(employment.salary_code_id) }}
          </span>
        </td>
        <td>
          <span class="do-pill do-pill--small do-custom-pill do-pill--multiline"
                v-if="showEmploymentSalaryFields(employment) && employment.default_omkostningssted">
            {{ getCostCenterName(employment.default_omkostningssted ) }}
          </span>
        </td>
        <td v-if="rowActionLabel" class="td-row-actions">
          <div class="row-action-btns">
            <button
              type="button"
              class="row-action-btn"
              @click.stop="$emit('removeEmployment', { user, employment })"
            >Fjern ansættelse</button>
            <button
              v-if="user.ssn"
              type="button"
              class="row-action-btn"
              @click.stop="setTheEditUser(user)"
            >Opret ny ansættelse</button>
          </div>
        </td>
      </tr>

      <tr v-if="user.v_employments.length === 0" @click.stop="setTheEditUser(user)">
        <td class="td-check">
          <!-- Ingen ansættelser at vælge -->
        </td>
        <td>
          <div class="name-cell">
            <b>{{ user.name }}</b>
          </div>
        </td>
        <td class="td-ssn" :class="{'td-organisation': !!user.ssn}">
          <span v-if="user.ssn">{{ formatSsn(user.ssn) }}</span>
          <span v-else class="text-link" @click.stop="$emit('editSsn', user)">Indtast CPR</span>
        </td>
        <td>
          <span
            v-if="rowActionLabel && user.ssn"
            class="text-link"
            @click.stop="setTheEditUser(user)"
          >{{ rowActionLabel }}</span>
        </td>
        <td></td>
        <td class="td-percent"></td>
        <td></td>
        <td></td>
        <td class="td-subsalary"></td>
        <td></td>
        <td></td>
        <td v-if="rowActionLabel" class="td-row-actions"></td>
      </tr>
      </tbody>
    </template>

    <!-- Simpel visning (uden integration) -->
    <template v-else>
      <tr v-for="user in filteredUsers" :key="user.id">
        <td colspan="1"></td>
        <td>{{ user.name }}</td>
        <td class="d-tags" colspan="6">
          <div class="d-tag"
               v-for="employment in user.v_employments"
               :key="employment.id"
               @click.stop="$emit('toggleEmployment', employment)">
            <input
              class="td-check__inline"
              type="checkbox"
              :checked="isEmploymentSelected(employment.id)"
              @click.stop
              readonly
            >
            {{ employment.title }}
            <div class="d-tag d-tag--inner" v-if="employment.organisation">{{ employment.organisation }}</div>
            <div class="do-pill do-pill--small do-pill--yellow" v-if="employment.manual_salary">Vikarløn</div>
          </div>
          <p class="italic font-small" v-if="user.v_employments.length === 0 && isShowNoEmploymentsText">Ingen
            ansættelser</p>
        </td>
      </tr>
    </template>
    </tbody>
  </table>
</template>

<script>
import AppToggle from '../../common/AppToggle.vue'
import moment from 'moment'

export default {
  components: {AppToggle},
  props: {
    filteredUsers: {
      default: () => []
    },
    selectedEmploymentIds: {
      type: Array,
      default: () => []
    },
    isShowNoEmploymentsText: {
      type: Boolean,
      default: false
    },
    isSalaryIntegrationActive: {
      type: Boolean,
      default: false
    },
    employmentTemplates: {
      type: Array,
      default: () => []
    },
    isShowEmploymentNumber: {
      type: Boolean,
      default: false
    },
    rowActionLabel: {
      type: String,
      default: 'Opret engangsydelse'
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    costCenters () {
      return this.$store.getters.costCentersAll
    },
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted
    },
    visibleEmploymentIds () {
      const ids = []
      this.filteredUsers.forEach(user => {
        (user.v_employments || []).forEach(e => ids.push(e.id))
      })
      return ids
    },
    selectedVisibleCount () {
      return this.visibleEmploymentIds.filter(id => this.selectedEmploymentIds.indexOf(id) > -1).length
    },
    isAllVisibleSelected () {
      return this.visibleEmploymentIds.length > 0 && this.selectedVisibleCount === this.visibleEmploymentIds.length
    },
    isIndeterminateVisible () {
      return this.selectedVisibleCount > 0 && this.selectedVisibleCount < this.visibleEmploymentIds.length
    }
  },
  methods: {
    formatEmploymentPeriod (employment) {
      const start = employment.start_date ? moment(employment.start_date).format('DD-MM-YYYY') : ''
      const end = employment.end_date ? moment(employment.end_date).format('DD-MM-YYYY') : ''
      if (!start && !end) return ''
      return `${start} – ${end}`
    },
    formatEmploymentPercent (percent) {
      if (percent == null || percent === '') return ''
      const number = parseFloat(percent)
      if (isNaN(number)) return percent
      return (number % 1 === 0 ? String(Math.round(number)) : String(number).replace('.', ',')) + '%'
    },
    formatSsn (ssn) {
      if (!ssn) return ''
      const digits = String(ssn).replace(/\D+/g, '')
      if (digits.length === 10) {
        return digits.slice(0, 6) + '-' + digits.slice(6)
      }
      return ssn
    },
    getCostCenterName (defaultOmkostningssted) {
      const costCenter = this.costCenters.find(costCenter => costCenter.code * 1 === defaultOmkostningssted)
      return costCenter ? costCenter.title : defaultOmkostningssted
    },
    getDefaultSalaryCodeName (salaryCodeId) {
      const salaryCode = this.salaryCodes.find(costCenter => costCenter.id === salaryCodeId)
      return salaryCode ? salaryCode.vTitle : salaryCodeId
    },
    setTheEditUser (user) {
      this.$emit('setTheEditUser', user)
    },
    isEmploymentSelected (employmentId) {
      return this.selectedEmploymentIds.indexOf(employmentId) > -1
    },
    hasStillingskategori (employment) {
      return !!employment && employment.employment_template_id != null && employment.employment_template_id !== undefined
    },
    isLocalEmployment (employment) {
      return !!employment && Number(employment.school_id) === Number(this.schoolId)
    },
    hasLocalStillingskategori (employment) {
      if (!this.hasStillingskategori(employment)) return false
      return this.employmentTemplates.some(template => Number(template.id) === Number(employment.employment_template_id))
    },
    isStillingskategoriFromOtherSchool (employment) {
      if (!this.hasStillingskategori(employment)) return false
      if (this.employmentTemplates.length > 0) {
        return !this.hasLocalStillingskategori(employment)
      }
      return !this.isLocalEmployment(employment)
    },
    showEmploymentSalaryFields (employment) {
      return !!employment && employment.manual_salary && this.hasLocalStillingskategori(employment)
    },
    onStillingskategoriClick (user, employment) {
      if (this.isStillingskategoriFromOtherSchool(employment)) return
      this.$emit('editEmployment', { user, employment })
    },
    setTbodyMaxHeight () {
      const tbody = this.$refs['d-b']
      if (!tbody) return
      const tbodyTop = tbody.getBoundingClientRect().top
      const windowHeight = window.innerHeight
      const maxHeight = windowHeight - tbodyTop - 20
      tbody.style.maxHeight = `${maxHeight}px`
    }
  },
  mounted () {
    this.setTbodyMaxHeight()
    window.addEventListener('resize', this.setTbodyMaxHeight)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.setTbodyMaxHeight)
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.salary-settings-users {
  background: #fff;
}

.do-table {
  border: 1px solid #e2e8f0;
  border-width: 0 1px 1px 1px;
  padding: 8px;
  width: 100%;
  background-color: #fff;
  border-collapse: collapse;
}

.do-table thead {
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  border-width: 1px 0;
  text-transform: uppercase;
  padding: 8px;
  font-weight: 700;
  text-align: left;
  color: #8898aa;
  font-size: 10px;
}

.do-table thead th:first-child {
  cursor: pointer;
}

.do-table thead th,
.do-table tbody td {
  padding: 0.5rem 0.5rem;
}

.do-table thead th:first-child,
.do-table tbody td:first-child {
  width: 80px;
}

.do-table tbody tr:nth-child(2n),
.user-group:nth-child(2n) {
  background-color: #f9fafb;
}

.do-table tbody tr .icon__bg-circle {
  opacity: 0;
}

.do-table tbody tr:hover .icon__bg-circle {
  opacity: 1;
}

.do-table tbody {
  display: block;
  overflow-y: scroll;
}

.do-table thead,
.do-table tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}


.d-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem
}

.d-tag {
  display: flex;
  align-items: center;
  padding: 1px 5px;
  border-radius: 4px;
  color: #333;
  border: 1px solid #c8d6e2;
  background: #f6f9fc;
  font-size: 11px;
  font-weight: 500;
  transition: background-color .02s;
}

.d-tag.d-tag--inner {
  font-size: 9px;
  border-left: 1px solid #ccc;
  background: none;
  border-width: 0 0 0 1px;
  border-radius: 0;
  line-height: 10px;
  margin-left: 5px;
  font-weight: normal;
  color: #868686;
}

.d-tag:hover .d-tag.d-tag--inner {
  color: #222;
}

.do-table tbody tr:hover {
  cursor: pointer;
  background: aliceblue;
}

.do-table.do-table__user-employments--import tr {
  background: transparent !important;
}

.do-table.do-table__user-employments--import tr:hover {
  background: aliceblue !important;
}

.th-check, .td-check {
  text-align: center;
  cursor: pointer;
  user-select: none;
  width: 34px !important;
  border-right: 1px solid #eee;
}

.th-check:hover, .td-check:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.check-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.th-check input[type="checkbox"],
.td-check input[type="checkbox"],
.td-check__inline {
  pointer-events: none;
  cursor: pointer;
}

.do-table.do-table__user-employments:not(.do-table__user-employments--import) tr th:last-child,
.do-table.do-table__user-employments:not(.do-table__user-employments--import) tr td:last-child {
  width: 40px;
}

.do-pill--small {
  padding: 0 0.5rem;
  font-size: 10px;
  line-height: 16px;
  margin-left: 0.5rem;
}

.d-tag--inline {
  display: inline-flex;
}

.d-tag--inline--clickable {
  cursor: pointer;
}

.d-tag--inline--clickable:hover {
  box-shadow: 0 0 1px 2px rgba(70, 138, 215, .56863);
}

.d-tag--from-other-school {
  opacity: 0.45;
  cursor: default;
}

.user-group {
  border-bottom: 1px solid #eee;
}

.td-organisation {
  opacity: 0.7;
  font-size: 11px;
}

.td-period {
  white-space: nowrap;
}

.td-percent {
  width: 100px;
  white-space: nowrap;
}

.toggle-flex {
  display: flex;
  align-items: center;
}

.toggle-flex--disabled {
  opacity: 0.15;
  cursor: not-allowed;
  background: transparent !important;
}

.input-group--flex {
  display: flex;
}

.do-custom-pill {
  padding: 0.25rem 0.5rem;
}

.do-pill--multiline {
  display: inline-block;
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
  margin-left: 0;
  text-align: left;
}

.td-subsalary {
  width: 80px;
}

.td-ssn {
  white-space: nowrap;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.th-row-actions {
  width: 250px;
}

.td-row-actions {
  width: 250px;
  text-align: right;
  position: relative;
}

.row-action-btns {
  display: flex;
  gap: 0.5rem;
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.row-action-btn {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  border: 1px solid #c8d6e2;
  background: #fff;
  color: var(--dark-blue);
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.12s ease, background-color 0.12s ease;
}

.row-action-btn:hover {
  background: #f0f8ff;
}

.do-table tbody tr:hover .row-action-btn {
  opacity: 1;
  pointer-events: auto;
}
</style>



// WEBPACK FOOTER //
// SalarySettingsUsersOpusTable.vue?2da26365